import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import CardsTable from "@/components/CardsTable";
import HandleCareerModal from "@/pages/Scouting/Players/Player/handleCareer";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ControlPointIcon from "@mui/icons-material/ControlPoint";

import {StaffAPI} from "@/api/staff";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

import pathUrl from "@/utils/path";

import "./index.scss";

function ManagerStaffPage(props) {
	const {handleNotification} = props;

	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openCareerDetail, setOpenCareerDetail] = useState(false);
	const [staffData, setStaffData] = useState({});

	const [initialData, setInitialData] = useState();

	const getStaff = async () => {
		setLoading(true);
		try {
			const response = await StaffAPI.getById(id);
			setStaffData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getStaff();
	}, [id]);

	const handleCareerDetail = async (data, id) => {
		setLoading(true);
		try {
			const body = {
				...data,
				staffId: staffData?.id,
				seasonId: data?.careerSeason?.value,
				clubId: data?.careerClub?.value,
				playerCategoryId: data?.careerCategory?.value,
			};

			id
				? await StaffAPI.updateCareer(staffData?.id, id, body)
				: await StaffAPI.createCareer(body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getStaff();
			setOpenCareerDetail(false);
			setInitialData();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteCareerDetail = async (id) => {
		setLoading(true);
		try {
			await StaffAPI.deleteCareer(staffData?.id, id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getStaff();
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const deleteStaff = async () => {
		setLoading(true);
		try {
			await StaffAPI.delete(staffData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/manager/staffs");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const mainConfiguration = [
		{value: staffData?.firstName, label: "Nome", size: "col-6 col-lg-4"},
		{
			value: staffData?.lastName,
			label: "Cognome",
			size: "col-6 col-lg-4",
		},
		{
			value: staffData?.dateOfBirth
				? moment(staffData?.dateOfBirth).format(dateFormat)
				: null,
			label: "Data di nascita",
			size: "col-6 col-lg-4",
		},
		{value: staffData?.unit?.name, label: "Area", size: "col-6 col-lg-4"},
		{value: staffData?.role?.name, label: "Ruolo", size: "col-6 col-lg-4"},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/manager/staffs"),
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: "Modifica",
			onClick: () => navigate("/manager/staffs/" + staffData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: "Elimina",
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const careerColumns = [
		{
			label: "Stagione",
			key: "season",
			customRender: (item) => item.season?.name,
		},
		{
			label: "Squadra",
			key: "club",
			customRender: (item) => item.club?.name,
		},
		{
			label: "Categoria",
			key: "playerCategory",
			customRender: (item) => item.playerCategory?.name,
		},
	];

	const breadcrumbsElements = [
		{label: "Staff", path: "/manager/staffs"},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={
					staffData
						? (staffData?.firstName || "") + " " + (staffData?.lastName || "")
						: ""
				}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-2">
										{staffData?.id && (
											<div
												className={
													"image-container" +
													(!staffData.imageUrl ? " generic" : "")
												}
											>
												<img
													src={
														!staffData.imageUrl
															? require("@/assets/images/manager/staff.png")
															: pathUrl +
																"/uploads/profile/staff/" +
																staffData.id?.toString() +
																"/" +
																staffData.imageUrl
													}
													alt=""
												/>
											</div>
										)}
									</div>
									<div className="col-sm-12 col-md-10">
										<ViewField configuration={mainConfiguration} />
									</div>
								</div>
							}
							openable={false}
						/>
					</div>
				</div>
				<div className="row no-margin mt-3">
					<div className="col-sm-12 padding-small">
						<CardsTable
							title="Carriera"
							fullHeight={false}
							middleHeight
							configurationColumns={careerColumns}
							data={staffData?.career}
							actions={[
								{
									icon: <ControlPointIcon />,
									onClick: () => setOpenCareerDetail(true),
								},
							]}
							handleEdit={(item) => {
								setInitialData(Object.assign({}, item));
								setOpenCareerDetail(true);
							}}
							handleDelete={(item) => {
								deleteCareerDetail(item.id);
							}}
							defaultSortedField="dateEvent"
							defaultSortedOrder="descend"
						/>
					</div>
				</div>
			</div>
			<VLFModal
				open={openHandleConfirm}
				title="Eliminazione"
				content={
					<div className="mt-2">
						Sei sicuro di voler eliminare questo elemento?
					</div>
				}
				confirm={() => deleteStaff()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel="Conferma"
			/>
			{openCareerDetail ? (
				<HandleCareerModal
					open={openCareerDetail}
					confirm={handleCareerDetail}
					close={() => {
						setOpenCareerDetail(false);
						setInitialData();
					}}
					initialData={initialData}
					handleNotification={handleNotification}
				/>
			) : null}
		</Page>
	);
}

export default ManagerStaffPage;
